import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useLegal } from '../lib/legalStrings';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Sparkles, Mail, Lock, User, ArrowLeft, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';

export default function AuthPage({ mode = 'login' }) {
  const { t, language } = useLanguage();
  const L = useLegal(language);
  const { login, register, googleLogin } = useAuth();
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [aiAcknowledged, setAiAcknowledged] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'signup' && !aiAcknowledged) {
      toast.error(L.aiDisclosure.signupAcknowledge);
      return;
    }
    if (mode === 'signup' && !legalAccepted) {
      toast.error(L.aiDisclosure.legalAcknowledge);
      return;
    }
    setIsLoading(true);

    try {
      if (mode === 'login') {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
      } else {
        await register(formData.email, formData.password, formData.name, {
          ai_terms_accepted: aiAcknowledged,
          marketing_consent: marketingConsent,
          language: language || 'en'
        });
        toast.success('Account created successfully!');
      }
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Something went wrong';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    googleLogin();
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Noise Overlay */}
      <div className="noise-overlay" />
      
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </Link>

          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-bold text-2xl">AIgnyx</span>
          </div>

          {/* Title */}
          <h1 className="font-heading text-3xl font-bold mb-2" data-testid="auth-title">
            {mode === 'login' ? t('auth.login') : t('auth.signup')}
          </h1>
          <p className="text-muted-foreground mb-8">
            {mode === 'login' 
              ? 'Welcome back! Enter your credentials to continue.'
              : 'Create an account to start generating ads with viral potential.'}
          </p>

          {/* Google Button */}
          <Button 
            variant="outline" 
            className="w-full h-12 mb-6"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('auth.orContinue')} {t('auth.google')}
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Or</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'signup' && (
              <div className="space-y-2">
                <Label htmlFor="name">{t('auth.name')}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input 
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className="pl-10 h-12"
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input 
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="pl-10 h-12"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input 
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pl-10 h-12"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
              </div>
            </div>

            {mode === 'signup' && (
              <div className="space-y-3 pt-1">
                {/* AI transparency notice — EU AI Act Article 50 */}
                <div className="flex gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20 text-xs text-muted-foreground leading-relaxed">
                  <Info className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <p>{L.aiDisclosure.signupNotice}</p>
                </div>

                {/* Required: acknowledgment of AI system */}
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={aiAcknowledged}
                    onCheckedChange={(v) => setAiAcknowledged(!!v)}
                    id="ai-ack"
                    data-testid="ai-ack-checkbox"
                    className="mt-0.5"
                  />
                  <span className="text-xs text-foreground leading-relaxed">
                    {L.aiDisclosure.signupAcknowledge}
                    <span className="text-destructive ml-0.5" aria-hidden="true">*</span>
                  </span>
                </label>

                {/* Required: acceptance of Privacy Policy + Cookie Policy */}
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={legalAccepted}
                    onCheckedChange={(v) => setLegalAccepted(!!v)}
                    id="legal-accept"
                    data-testid="legal-accept-checkbox"
                    className="mt-0.5"
                  />
                  <span className="text-xs text-foreground leading-relaxed">
                    {L.aiDisclosure.legalAcknowledgePrefix}{' '}
                    <Link
                      to="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                      data-testid="signup-privacy-link"
                    >
                      {L.aiDisclosure.legalPrivacyLabel}
                    </Link>
                    {' '}{L.aiDisclosure.legalAndWord}{' '}
                    <Link
                      to="/cookies"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                      data-testid="signup-cookies-link"
                    >
                      {L.aiDisclosure.legalCookieLabel}
                    </Link>
                    .
                    <span className="text-destructive ml-0.5" aria-hidden="true">*</span>
                  </span>
                </label>

                {/* Optional: marketing consent */}
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={marketingConsent}
                    onCheckedChange={(v) => setMarketingConsent(!!v)}
                    id="marketing-consent"
                    data-testid="marketing-consent-checkbox"
                    className="mt-0.5"
                  />
                  <span className="text-xs text-muted-foreground leading-relaxed">
                    {L.aiDisclosure.marketingOptIn}
                  </span>
                </label>
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full h-12 rounded-full"
              disabled={isLoading || (mode === 'signup' && (!aiAcknowledged || !legalAccepted))}
              data-testid="submit-btn"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                mode === 'login' ? t('auth.login') : t('auth.signup')
              )}
            </Button>

            {mode === 'login' && (
              <div className="text-center">
                <Link
                  to="/forgot-password"
                  className="text-sm text-muted-foreground hover:text-primary underline underline-offset-2"
                  data-testid="forgot-password-link"
                >
                  {t('auth.forgotPassword') || 'Forgot password?'}
                </Link>
              </div>
            )}
          </form>

          {/* Switch Mode */}
          <p className="mt-6 text-center text-sm text-muted-foreground">
            {mode === 'login' ? t('auth.noAccount') : t('auth.hasAccount')}{' '}
            <Link 
              to={mode === 'login' ? '/signup' : '/login'} 
              className="text-primary hover:underline font-medium"
              data-testid="switch-mode-link"
            >
              {mode === 'login' ? t('auth.signup') : t('auth.login')}
            </Link>
          </p>

          {/* Mini legal strip — reachable from every auth page */}
          <div className="mt-8 pt-6 border-t border-border/60 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px] text-muted-foreground">
            <Link to="/privacy" className="hover:text-primary" data-testid="auth-legal-privacy">
              {t('footer.privacy')}
            </Link>
            <span aria-hidden="true">·</span>
            <Link to="/terms" className="hover:text-primary" data-testid="auth-legal-terms">
              {t('footer.terms')}
            </Link>
            <span aria-hidden="true">·</span>
            <Link to="/cookies" className="hover:text-primary" data-testid="auth-legal-cookies">
              {t('footer.cookies')}
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Right Panel - Visual */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-primary/5 relative overflow-hidden">
        <div className="hero-glow opacity-30" />
        <div className="relative z-10 text-center p-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="w-32 h-32 rounded-3xl bg-primary/20 flex items-center justify-center mx-auto mb-8">
              <Sparkles className="w-16 h-16 text-primary" />
            </div>
            <h2 className="font-heading text-2xl font-bold mb-4">Generate Ads with Viral Potential</h2>
            <p className="text-muted-foreground max-w-sm">
              Create high-converting UGC ads for TikTok, Instagram, and YouTube in seconds.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
